import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const codeSchema = z.object({
  code: z
    .string()
    .trim()
    .min(4, "招待コードを入力してください")
    .max(64, "招待コードが長すぎます"),
});

type RedeemResult =
  | { ok: true }
  | { ok: false; error: string; message: string };

function mapError(message: string): { error: string; message: string } {
  switch (message) {
    case "INVALID_CODE":
      return { error: "INVALID_CODE", message: "招待コードが見つかりません" };
    case "INACTIVE_CODE":
      return { error: "INACTIVE_CODE", message: "この招待コードは無効化されています" };
    case "EXPIRED_CODE":
      return { error: "EXPIRED_CODE", message: "この招待コードは有効期限切れです" };
    case "CODE_LIMIT_REACHED":
      return { error: "CODE_LIMIT_REACHED", message: "この招待コードは利用上限に達しています" };
    default:
      return { error: "UNKNOWN", message: "招待コードの確認に失敗しました" };
  }
}

/**
 * Validate without consuming. Used to pre-check before OAuth redirect.
 */
export const validateInviteCode = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => codeSchema.parse(data))
  .handler(async ({ data }): Promise<RedeemResult> => {
    const { error } = await supabaseAdmin.rpc("validate_invite_code", {
      p_code: data.code,
    });
    if (error) {
      return { ok: false, ...mapError(error.message) };
    }
    return { ok: true };
  });

/**
 * Validate AND consume (atomic). Call only after a successful signup.
 */
export const redeemInviteCode = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => codeSchema.parse(data))
  .handler(async ({ data }): Promise<RedeemResult> => {
    const { error } = await supabaseAdmin.rpc("redeem_invite_code", {
      p_code: data.code,
    });
    if (error) {
      return { ok: false, ...mapError(error.message) };
    }
    return { ok: true };
  });
